import { DateFormat, ds, ts } from '@/plugins/i18n-formatted'
import Club from '@model/Club'
import EventBoat from '@model/EventBoat'
import EventCategory from '@model/EventCategory'
import EventClass from '@model/EventClass'
import EventCourse from '@model/EventCourse'
import EventLifeguard from '@model/EventLifeguard'
import EventRace from '@model/EventRace'
import EventReferee from '@model/EventReferee'
import Venue from '@model/Venue'
import EventsRepo from '@repo/EventsRepo'
import { Model, useRepo } from 'pinia-orm'
import { DateCast } from 'pinia-orm/casts'
import { Attr, BelongsTo, Bool, Cast, HasMany, Num, OnDelete, Str } from 'pinia-orm/decorators'

export enum ResultsEnterModel {
	EnterNumByNum,
	EnterDirectClick,
	EnterBigField
}

export default class Event extends Model {
	static entity = 'events'

	@Num( 0, { notNullable: true } ) declare id: number
	@Str( '', { notNullable: true } ) declare title: string
	@Num( 0, { notNullable: true } ) declare ctlId: number
	@Cast( () => DateCast ) @Attr( '' ) declare from: string
	@Cast( () => DateCast ) @Attr( null ) declare to: string | null
	@Num( null ) declare clubId: number | null
	@BelongsTo( () => Club, 'clubId' ) declare club: Club | null
	@Str( null ) declare organizer: string | null
	@Num( null ) declare venueId: number | null
	@BelongsTo( () => Venue, 'venueId' ) declare venue: Venue | null
	@Str( null ) declare otherVenue: string | null
	@Str( null ) declare registrationUrl: string | null
	@Str( null ) declare noticeboardUrl: string | null
	@Str( null ) declare description: string | null
	@Str( null ) declare sponsor: string | null
	@HasMany( () => EventClass, 'eventId' ) @OnDelete( 'cascade' ) declare classes: EventClass[]
	@Str( null ) declare director: string | null
	@HasMany( () => EventReferee, 'eventId' ) @OnDelete( 'cascade' ) declare referees: EventReferee[]
	@HasMany( () => EventLifeguard, 'eventId' ) @OnDelete( 'cascade' ) declare lifeguards: EventLifeguard[]
	@HasMany( () => EventBoat, 'eventId' ) @OnDelete( 'cascade' ) declare boats: EventBoat[]
	@HasMany( () => EventRace, 'eventId' ) @OnDelete( 'cascade' ) declare races: EventRace[]
	@Num( null ) declare plannedRaces: number | null
	@Bool( false ) declare noLifeguards: boolean
	@Str( null ) declare rrsUuid: string | null
	@HasMany( () => EventCourse, 'eventId' ) @OnDelete( 'cascade' ) declare courses: EventCourse[]

	@Cast( () => DateCast ) @Attr( '' ) declare created: string
	@Cast( () => DateCast ) @Attr( '' ) declare lastOpened: string
	@Str( 'EventConfigurationTab' ) declare currentTab: string
	@Bool( false ) declare ignoreUpdateMembers: boolean
	@Bool( false ) declare showOnlyCaptains: boolean
	@Num( ResultsEnterModel.EnterNumByNum ) declare currentResultsEnterMode: number
	@Bool( false ) declare forceEnglishReports: boolean

	@Bool( false ) declare modified: boolean

	get dump () {
		const current = useRepo( Event ).withAllRecursive().find( this.id )

		if ( current === null )
			return null

		return {
			ctl: current.ctlId,
			title: current.title,
			from: ds( current.from, DateFormat.SystemDate ),
			to: current.to === null ? null : ds( current.to, DateFormat.SystemDate ),
			clubId: current.clubId,
			organizer: current.organizer,
			venueId: current.venueId,
			otherVenue: current.otherVenue,
			registrationUrl: current.registrationUrl,
			noticeboardUrl: current.noticeboardUrl,
			description: current.description,
			sponsor: current.sponsor,
			director: current.director,
			plannedRaces: current.plannedRaces,
			created: ds( current.created, DateFormat.System ),
			lastOpened: ds( current.lastOpened, DateFormat.System ),
			ignoreUpdateMembers: current.ignoreUpdateMembers,
			noLifeguards: current.noLifeguards,
			rrsUuid: current.rrsUuid,
			forceEnglishReports: current.forceEnglishReports,
			classes: current.classes.map( cl => cl.dump ),
			referees: current.referees.map( referee => referee.dump ),
			lifeguards: current.lifeguards.map( lifeguard => lifeguard.dump ),
			boats: current.boats.map( boat => boat.dump ),
			races: current.races.map( race => race.dump ),
			courses: current.courses.map( course => course.dump ),
			_computed: current.classes.reduce( ( obj, cl ) => ( {
				...obj, [ cl.boatClassId ]: this.results( cl.boatClassId, null, false ).map( res => {
					const classResults = res

					classResults.boat = {
						id: classResults.boat.id,
						country: classResults.boat.country,
						sailNumber: classResults.boat.sailNumber,
					}

					return classResults
				} ),
			} ), {} ),
		}
	}

	get fullTitle () {
		return ts( 'ctl', { ctlId: this.ctlId } ) + ' ' + this.title + ' (' + this.dateRange + ')'
	}

	get dateRange () {
		return ds( new Date( this.from ), ( this.to != null && !this.from.isSameAs( this.to ) ) ? DateFormat.EventFrom : DateFormat.EventTo )
			+ ( ( this.to == null || this.from.isSameAs( this.to ) ) ? '' : ' – ' + ds( new Date( this.to ), DateFormat.EventTo ) )
	}

	get orderedClasses () {
		return this.classes.sort( ( a, b ) => ( a.weight == b.weight ? a.shortcut.localeCompare( b.shortcut ) : b.weight - a.weight ) )
	}

	get orderedCourses () {
		return this.courses.sort( ( a, b ) => ( a.title.localeCompare( b.title ) ) )
	}

	get useCourses () {
		return this.courses.length > 0
	}

	get orderedClassesList () {
		return this.orderedClasses.map( ( eventClass ) => ( {
			value: eventClass.boatClass.id,
			label: eventClass.boatClass.name,
			shortLabel: eventClass.boatClass.shortcut,
		} ) )
	}

	get orderedReferees () {
		return this.referees.sort( ( a, b ) => (
			a.refereeRole.points == b.refereeRole.points
				? ( a.refereeRole.order === b.refereeRole.order
						? ( a.refereeRole.role === b.refereeRole.role
								? a.referee.reverseFullName.localeCompare( b.referee.reverseFullName )
								: a.refereeRole.role.localeCompare( b.refereeRole.role )
						)
						: b.refereeRole.order - a.refereeRole.order
				)
				: b.refereeRole.points - a.refereeRole.points
		) )
	}

	get orderedLifeguards () {
		return this.lifeguards.sort( ( a, b ) => ( a.lifeguardRole.points == b.lifeguardRole.points ? a.lifeguard.reverseFullName.localeCompare( b.lifeguard.reverseFullName ) : b.lifeguardRole.points - a.lifeguardRole.points ) )
	}

	filteredBoats ( boatClass = null ) {
		const eventId = this.id

		if ( boatClass === null ) {
			if ( this.boats !== undefined )
				return this.boats

			return useRepo( EventBoat ).withAllRecursive().where( 'eventId', eventId ).get()
		}

		return useRepo( EventBoat ).withAllRecursive().where( 'eventId', eventId ).where( 'boatClassId', boatClass ).get()
	}

	orderedBoats ( boatClass = null, ignoreClass = false ) {
		const classesWeights = Object.fromEntries( this.classes.map( eventClass => ( [ eventClass.boatClass?.id ?? eventClass.boatClassId, eventClass.weight ] ) ) )

		return this.filteredBoats( boatClass ).sort( ( a, b ) => (
			ignoreClass
				? a.sailNoSortable.localeCompare( b.sailNoSortable )
				: (
					classesWeights[ a.boatClass.id ] == classesWeights[ b.boatClass.id ]
						? ( a.boatClass.id === b.boatClass.id
							? ( a.sailNoSortable.localeCompare( b.sailNoSortable ) )
							: a.boatClass.name.localeCompare( b.boatClass.name ) )
						: ( classesWeights[ b.boatClass.id ] - classesWeights[ a.boatClass.id ] ) ) ) )
	}

	availableRaces ( boatClass = null, detail = true ) {
		let races

		if ( boatClass !== null ) {
			races = useRepo( EventRace ).withAllRecursive().where( 'eventId', this.id ).get()
										.filter( race => race.results.some( result => result.boat?.boatClassId === boatClass ) )
		}
		else if ( !detail ) {
			return useRepo( EventRace ).where( 'eventId', this.id ).get()
		}
		else {
			races = useRepo( EventRace ).withAllRecursive().where( 'eventId', this.id ).get()
		}

		if ( !detail )
			return races

		return races.sort( ( a, b ) => ( a.race - b.race ) )
	}

	unfinishedBoats ( race = null, boatClass = null ) {
		const finishedBoats = race !== null ? this.races.filter( er => er.race === race.race )[ 0 ]?.results.map( result => result.boat.id ) : []
		const skippedClasses = race !== null ? race.skippedClasses.map( c => c.boatClassId ) : []

		return this.orderedBoats( boatClass, true )
				   .filter( boat => finishedBoats.indexOf( boat.id ) === -1 && skippedClasses.indexOf( boat.boatClassId ) === -1 )
				   .map( ( boat ) => ( {
					   value: boat.id,
					   label: boat.sailNo
						   + ( ( boat.boatClass.useBoatNames && boat.name !== null && boat.name !== undefined && boat.name.length > 0 ) ? ' ' + boat.name : '' )
						   + ( boatClass === null ? ' (' + boat.boatClass.name + ')' : '' )
						   + ( boat.captain !== null ? ' – kpt. ' + boat.captain.reverseFullName : '' ),
					   sailNumber: boat.sailNumber
				   } ) )
	}

	getClass ( boatClass ) {
		const eventId = this.id

		if ( this.classes === undefined )
			return useRepo( EventClass ).withAllRecursive().where( 'eventId', eventId ).where( 'boatClassId', boatClass ).first()

		const foundClass = this.classes.filter( cl => cl.boatClassId === boatClass )

		if ( foundClass.length === 0 )
			return null

		return foundClass[ 0 ]
	}

	get venueTitle () {
		return this.venueId != null ? this.venue?.localizedName : this.otherVenue
	}

	get clubTitle () {
		return this.clubId != null ? this.club?.id + ' ' + this.club?.name : this.organizer
	}

	get clubTitleShort () {
		return this.clubId != null ? this.club?.id + ' ' + this.club?.shortcut : this.organizer
	}

	get mainReferee () {
		const eventReferee = useRepo( EventReferee ).where( 'eventId', this.id ).where( 'refereeRoleId', 1 ).with( 'referee' ).first()

		if ( eventReferee === null )
			return null

		return eventReferee.referee
	}

	classAttended ( boatClassId ) {
		return this.classes.find( ( eventClass ) => ( eventClass.boatClassId === boatClassId ) )
	}

	results ( boatClass, categoryId = null, details = true ) {
		if ( boatClass === null )
			return []

		const eventClass = this.getClass( boatClass )

		if ( eventClass === null )
			return []

		const discarded = eventClass.discarded

		let data = useRepo( EventBoat ).withAllRecursive().where( 'eventId', this.id ).where( 'boatClassId', boatClass ).get()

		if ( categoryId !== null ) {
			const category = useRepo( EventCategory ).with( 'eventClass', ( query ) => {
				query.with( 'event' )
			} ).find( categoryId )

			data = data.filter( ( boat ) => ( category.isBoatInCategory( boat ) ) )
		}

		const availableRaces = this.availableRaces( boatClass )

		const totalBoats = eventClass.totalBoats

		const eventClassRepo = useRepo( EventClass ).withAllRecursive().where( 'eventId', this.id ).where( 'boatClassId', boatClass ).first()

		const scoringSystem = eventClass.boatClass.scoringSystem            // A or B

		const results = data.map( boat => {
			const discardedRaces = boat.discardedRaces( discarded, availableRaces, totalBoats, eventClassRepo )

			const results = boat.cachedResults( availableRaces, eventClassRepo, totalBoats, details ).map( result => {
				result.isDiscarded = discardedRaces.indexOf( result.race ) >= 0

				return result
			} )

			// b83 eq a82 podle pravidel, takze ho nemusim pocitat
			return {
				boat: boat,
				results: results,
				points: results.reduce( ( sum, race ) => sum + ( race.isDiscarded ? 0 : race.points ), 0 ),
				isDncOnly: results.filter( race => !race.dnc ).length === 0,
				a81: results.filter( race => !race.isDiscarded ).map( race => race.points )
							.sort( ( a, b ) => ( a - b ) ).reduce( ( results, result ) => results + ' ' + String( result ).padStart( 4, '0' ), '' ).trim(),
				a82: results.map( race => race.points ).reduce( ( results, result ) => String( result ).padStart( 4, '0' ) + ' ' + results, '' ).trim(),
				b81: results.filter( race => race.isDiscarded ).map( race => race.points )
							.sort( ( a, b ) => ( a - b ) ).reduce( ( results, result ) => results + ' ' + String( result ).padStart( 4, '0' ), '' ).trim(),
				b82: results.map( race => race.points )
							.sort( ( a, b ) => ( a - b ) ).reduce( ( results, result ) => results + ' ' + String( result ).padStart( 4, '0' ), '' ).trim(),
			}
		} ).sort( ( a, b ) => (
				!a.isDncOnly && !b.isDncOnly
					? ( a.points !== b.points
							? a.points - b.points
							: ( scoringSystem === 'A'
									? ( a.a81 !== b.a81 ? ( a.a81 > b.a81 ? 1 : -1 ) : ( a.a82 > b.a82 ? 1 : -1 ) )
									: ( a.b81 !== b.b81 ? ( a.b81 > b.b81 ? 1 : -1 ) : ( a.b82 !== b.b82 ? ( a.b82 > b.b82 ? 1 : -1 ) : ( a.a82 > b.a82 ? 1 : -1 ) ) )
							)
					)
					: ( a.isDncOnly && b.isDncOnly ? 0 : ( a.isDncOnly ? 1 : -1 ) )
			),
		)

		let lastDistinguisher = ''
		let index = 0

		results.forEach( function ( result ) {
			let distinguisher = result.points + '|' + result.a81 + '|' + result.a82

			if ( distinguisher !== lastDistinguisher ) {
				++index
				lastDistinguisher = distinguisher
			}

			result.index = index
		} )

		const totalRacedBoats = eventClass.totalRacedBoats
		const calculatedWeight = eventClass.calculatedWeight

		results.forEach( function ( result ) {
			result.logPoints = result.isDncOnly ? 0 : Math.round( ( Math.log10( totalRacedBoats / result.index ) * 1000 + 101 ) * calculatedWeight )
		} )

		return results
	}

	allBoatsOrdered ( boatClass = null ) {
		return this.filteredBoats( ( boatClass === null || boatClass === '-all' ) ? null : boatClass )
				   .map( boat => ( { ...boat, sailNo: boat.sailNo, finished: false } ) )
				   .sort( ( a, b ) => ( a.sailNumber - b.sailNumber ) )
	}

	setModified ( value: boolean = true ) {
		useRepo( EventsRepo ).save( { id: this.id, modified: value } )
	}

	static piniaOptions = {
		persist: true,
	}
}
